import { useEffect, useRef, useState } from "react";
import { Camera, Eraser, Loader2, PenLine, X } from "lucide-react";

// Popup « Signature de livraison » (demande du propriétaire, 2026-10-08) : affiché
// quand on marque « Livré » une livraison en MAIN PROPRE ou un RAMASSAGE. Le client
// signe à l'écran (doigt sur téléphone, ou souris) + nom en toutes lettres ; PHOTO
// DE LIVRAISON facultative (caméra du téléphone ou fichier — renforce la preuve) ;
// saut possible via « Sans signature » avec raison OBLIGATOIRE. Une fois signée, la
// facture finale (avec signature et photo) part au client (copie à info@trophee.ca).
export default function SignatureLivraison({ titre, sousTitre, busy, sansCourrielDefaut, onSigner, onSansSignature, onFermer }) {
  const canvasRef = useRef(null);
  const photoRef = useRef(null);
  const dessin = useRef(false);
  const [aDessine, setADessine] = useState(false);
  const [nom, setNom] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [skipOuvert, setSkipOuvert] = useState(false);
  const [raison, setRaison] = useState("");
  // Option « Ne pas envoyer de courriel au client » CONSERVÉE dans le popup
  // (demande du propriétaire, 2026-10-08) — suit la case du dossier si déjà cochée
  const [sansCourriel, setSansCourriel] = useState(!!sansCourrielDefaut);

  useEffect(() => () => { if (photoUrl) URL.revokeObjectURL(photoUrl); }, [photoUrl]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const w = c.offsetWidth, h = 180;
    c.width = w * ratio;
    c.height = h * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = "#14213d";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const debut = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    dessin.current = true;
  };
  const bouge = (e) => {
    if (!dessin.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setADessine(true);
  };
  const fin = () => { dessin.current = false; };
  const effacer = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.offsetWidth, 180);
    setADessine(false);
  };

  const confirmer = () => {
    canvasRef.current.toBlob((b) => {
      if (b) onSigner(new File([b], "signature.png", { type: "image/png" }), nom.trim(), photo, sansCourriel);
    }, "image/png");
  };

  const choisirPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhoto(f);
    setPhotoUrl(URL.createObjectURL(f));
    e.target.value = "";
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="signature-modal">
      <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={onFermer} aria-label="Fermer" />
      <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto">
        <button onClick={onFermer} data-testid="signature-close"
          className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
          <X className="w-5 h-5" />
        </button>
        <h3 className="font-serif text-2xl mb-1 flex items-center gap-2">
          <PenLine className="w-5 h-5 text-gold" /> {titre}
        </h3>
        <p className="text-xs text-ivory/55 mb-5">{sousTitre}</p>

        {!skipOuvert ? (
          <>
            <p className="text-[11px] uppercase tracking-widest text-gold mb-2">Signature du client</p>
            <canvas ref={canvasRef} data-testid="signature-canvas"
              onPointerDown={debut} onPointerMove={bouge} onPointerUp={fin} onPointerLeave={fin}
              className="w-full h-[180px] rounded-xl border border-ivory/20 bg-white cursor-crosshair touch-none" />
            <div className="flex justify-end mt-1.5 mb-4">
              <button type="button" onClick={effacer} data-testid="signature-effacer"
                className="inline-flex items-center gap-1.5 text-xs text-ivory/60 hover:text-gold transition-colors">
                <Eraser className="w-3.5 h-3.5" /> Effacer
              </button>
            </div>
            <input type="text" value={nom} onChange={(e) => setNom(e.target.value)} maxLength={80}
              data-testid="signature-nom" placeholder="Nom en toutes lettres du signataire"
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm text-ivory focus:border-gold focus:outline-none transition-colors mb-4" />
            {/* Photo de livraison DEMANDÉE explicitement (demande du propriétaire,
                2026-10-08) — présentée comme une étape à part entière, mais reste
                sautable. Sur téléphone, « capture » ouvre directement la caméra. */}
            <p className="text-[11px] uppercase tracking-widest text-gold mb-2">
              Photo de livraison <span className="normal-case tracking-normal text-ivory/50">— recommandée (facultative)</span>
            </p>
            <input ref={photoRef} type="file" accept="image/*" capture="environment"
              onChange={choisirPhoto} className="hidden" data-testid="signature-photo-input" />
            {photoUrl ? (
              <div className="flex items-center gap-3 mb-4 rounded-lg border border-gold/25 bg-gold/5 p-2.5"
                data-testid="signature-photo-apercu">
                <img src={photoUrl} alt="Photo de livraison" className="h-14 w-14 object-cover rounded-md border border-ivory/15" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-ivory/80 truncate">{photo?.name}</p>
                  <p className="text-[10px] text-ivory/50">Photo jointe à la preuve de livraison</p>
                </div>
                <button type="button" data-testid="signature-photo-retirer"
                  onClick={() => { URL.revokeObjectURL(photoUrl); setPhoto(null); setPhotoUrl(""); }}
                  className="text-ivory/50 hover:text-red-400 transition-colors" aria-label="Retirer la photo">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => photoRef.current?.click()} data-testid="signature-photo-btn"
                className="w-full inline-flex items-center justify-center gap-2 border border-gold/40 bg-gold/10 text-gold px-4 py-3 rounded-lg text-sm font-medium hover:bg-gold hover:text-ink transition-colors mb-1.5">
                <Camera className="w-4 h-4" /> Prendre / choisir la photo de livraison
              </button>
            )}
            {!photoUrl && (
              <p className="text-[10px] text-ivory/45 mb-4">
                Recommandée : elle renforce la preuve en cas de litige (colis remis, état, lieu).
              </p>
            )}
            <button type="button" onClick={confirmer} disabled={busy || !aDessine || !nom.trim()}
              data-testid="signature-confirmer"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
              Signer et marquer « Livré »
            </button>
            <label className="flex items-center gap-2 mt-3 cursor-pointer select-none" data-testid="signature-sans-courriel">
              <input type="checkbox" checked={sansCourriel} onChange={(e) => setSansCourriel(e.target.checked)}
                className="w-3.5 h-3.5 accent-[#d4af37]" />
              <span className="text-[11px] text-ivory/60">Ne pas envoyer de courriel au client (facture finale / avis)</span>
            </label>
            <button type="button" onClick={() => setSkipOuvert(true)} data-testid="signature-sans"
              className="w-full mt-2.5 text-xs text-ivory/55 hover:text-gold transition-colors py-2">
              Sans signature (client pressé, refus de signer…)
            </button>
          </>
        ) : (
          <>
            <p className="text-[11px] uppercase tracking-widest text-gold mb-2">Sans signature — pourquoi ?</p>
            <textarea value={raison} onChange={(e) => setRaison(e.target.value)} rows={3} maxLength={300}
              data-testid="signature-raison" placeholder="Raison obligatoire (ex. client pressé, remise au garage…)"
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm text-ivory focus:border-gold focus:outline-none transition-colors mb-4" />
            <button type="button" onClick={() => onSansSignature(raison.trim(), sansCourriel)} disabled={busy || raison.trim().length < 3}
              data-testid="signature-sans-confirmer"
              className="w-full inline-flex items-center justify-center gap-2 border border-gold/50 text-gold font-semibold px-6 py-3 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Marquer « Livré » sans signature
            </button>
            <label className="flex items-center gap-2 mt-3 cursor-pointer select-none" data-testid="signature-sans-courriel-skip">
              <input type="checkbox" checked={sansCourriel} onChange={(e) => setSansCourriel(e.target.checked)}
                className="w-3.5 h-3.5 accent-[#d4af37]" />
              <span className="text-[11px] text-ivory/60">Ne pas envoyer de courriel au client (avis)</span>
            </label>
            <button type="button" onClick={() => setSkipOuvert(false)} data-testid="signature-retour"
              className="w-full mt-2.5 text-xs text-ivory/55 hover:text-gold transition-colors py-2">
              ← Retour à la signature
            </button>
          </>
        )}
      </div>
    </div>
  );
}
